import { useDesktopLayoutStore } from "@/lib/desktop-layout/store";
import type { OpenedTrade } from "@/vybe-trader-client/opened-trades/types";
import { useChartStore3 } from "@/vybe-trader-client/chart/stores/useChartStore3";
import { useSymbolsStore } from "@/vybe-trader-client/symbols/stores/useSymbolsStore";
import { getVisibleChartPanelIds } from "./getVisibleChartPanelIds";

export interface FocusOpenTradeOnDesktopChartsOptions {
  activateSlPlacement?: boolean;
  activateTpPlacement?: boolean;
}

/**
 * Focus the desktop chart area on `trade`: prefer a visible panel that already
 * shows `trade.symbol`, else load the symbol on the focused panel. Highlights
 * the trade via chart store pending focus (see Chart.tsx).
 */
export function focusOpenTradeOnDesktopCharts(
  trade: Pick<
    OpenedTrade,
    "symbol" | "ticket" | "stopLoss" | "takeProfit"
  >,
  options?: FocusOpenTradeOnDesktopChartsOptions,
): void {
  const layoutMode = useDesktopLayoutStore.getState().chartLayoutMode;
  const visible = getVisibleChartPanelIds(layoutMode);
  const slots = useChartStore3.getState().slots;
  const focused = useDesktopLayoutStore.getState().focusedChartPanelId;

  const matching = visible.filter(
    (id) => slots[id].selectedSymbol?.currency === trade.symbol,
  );

  const targetPanel =
    matching.length > 0
      ? matching.includes(focused)
        ? focused
        : (matching.reduce((a, b) => (Math.min(a, b) as typeof focused)))
      : focused;

  useDesktopLayoutStore.getState().setFocusedChartPanelId(targetPanel);

  if (matching.length === 0) {
    const symbol = useSymbolsStore.getState().byCurrency.get(trade.symbol);
    if (!symbol) {
      console.warn(
        "[focusOpenTradeOnDesktopCharts] Unknown symbol in catalog:",
        trade.symbol,
      );
      return;
    }
    useChartStore3.getState().setSelectedSymbol(symbol, targetPanel);
  }

  useChartStore3.getState().requestTradeChartFocus({
    panelId: targetPanel,
    ticket: trade.ticket.toString(),
    symbolCurrency: trade.symbol,
    activateSlPlacement: options?.activateSlPlacement,
    activateTpPlacement: options?.activateTpPlacement,
  });
}
